import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import axios from "axios";
import { PageHeader, StatusBadge, Loader, EmptyState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RepurposePanel from "@/components/RepurposePanel";
import VideoCreator from "@/components/VideoCreator";
import { Library as LibraryIcon, Trash2, Upload, Youtube, ExternalLink, FileVideo } from "lucide-react";

export default function LibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [file, setFile] = useState(null);
  const [privacy, setPrivacy] = useState("private");
  const [publishAt, setPublishAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ytConnected, setYtConnected] = useState(false);

  const load = () => api.get("/content").then((r) => setItems(r.data.content)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setYtConnected(r.data.youtube_connected));
  }, []);

  const open = async (id) => {
    setActive(id); setDetail(null); setFile(null);
    const r = await api.get(`/content/${id}`);
    setDetail(r.data);
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/content/${id}`);
    setItems((p) => p.filter((x) => x.id !== id));
    toast.success("Package deleted");
  };

  const upload = async () => {
    if (!file) { toast.error("Select a video file first"); return; }
    if (!ytConnected) { toast.error("Connect YouTube in Settings first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("package_id", detail.id);
    fd.append("privacy", privacy);
    if (publishAt) fd.append("publish_at", new Date(publishAt).toISOString());
    fd.append("video", file);
    try {
      const r = await axios.post(`${API}/youtube/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to YouTube!");
      setDetail((d) => ({ ...d, status: "uploaded", youtube_video_id: r.data.video_id }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader eyebrow="Your Content" title="Library" testid="library-header"
        subtitle="All your generated video packages. Open one to review, attach your finished video and publish to YouTube." />

      {loading ? <Loader label="Loading library" /> : items.length === 0 ? (
        <EmptyState icon={LibraryIcon} title="Your library is empty" hint="Generate video packages in the Content Studio and they'll show up here." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 fade-up">
          {items.map((c) => (
            <div key={c.id} onClick={() => open(c.id)} data-testid={`library-card-${c.id}`}
              className="bg-surface border border-white/10 rounded-md overflow-hidden hover:border-white/25 hover:-translate-y-1 transition-all duration-200 cursor-pointer group">
              <div className="aspect-video bg-[#0A0A0A] relative overflow-hidden">
                {c.has_thumbnail ? (
                  <img src={`${API}/content/${c.id}/thumbnail`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/15"><FileVideo className="w-10 h-10" strokeWidth={1} /></div>
                )}
                <div className="absolute top-2 right-2"><StatusBadge status={c.status} /></div>
              </div>
              <div className="p-4">
                <div className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-brand transition-colors">{(c.titles && c.titles[0]) || c.topic}</div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[10px] font-mono text-white/30 uppercase tracking-wider">{c.niche || "general"}</span>
                  <button onClick={(e) => del(c.id, e)} data-testid={`delete-pkg-${c.id}`} className="text-white/30 hover:text-brand transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="package-dialog">
          {!detail ? (
            <>
              <DialogHeader><DialogTitle className="sr-only">Loading package</DialogTitle></DialogHeader>
              <Loader label="Loading" />
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl pr-8 leading-tight">{(detail.titles && detail.titles[0]) || detail.topic}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {detail.thumbnail_base64 && (
                  <img src={`data:image/png;base64,${detail.thumbnail_base64}`} alt="thumb" className="w-full rounded border border-white/10 aspect-video object-cover" />
                )}
                <div className="flex items-center gap-2">
                  <StatusBadge status={detail.status} />
                  {detail.youtube_video_id && (
                    <a href={`https://youtu.be/${detail.youtube_video_id}`} target="_blank" rel="noopener noreferrer" data-testid="view-on-youtube"
                      className="text-xs text-signal-blue hover:underline flex items-center gap-1 font-mono">
                      <ExternalLink className="w-3 h-3" /> View on YouTube
                    </a>
                  )}
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono mb-1">Hook</div>
                  <p className="text-sm text-white/80 italic">"{detail.hook}"</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono mb-1">Script</div>
                  <pre className="text-sm text-white/70 whitespace-pre-wrap font-sans leading-relaxed max-h-52 overflow-y-auto bg-[#0A0A0A] rounded p-3 border border-white/5">{detail.script}</pre>
                </div>

                <RepurposePanel pkg={detail} onUpdate={(platforms) => setDetail((d) => ({ ...d, repurpose: platforms }))} />

                <VideoCreator key={detail.id} pkg={detail} />

                {detail.status !== "uploaded" && (
                  <div className="border-t border-white/10 pt-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-3 flex items-center gap-2"><Youtube className="w-4 h-4 text-brand" /> Publish to YouTube</div>
                    {!ytConnected && <p className="text-xs text-signal-yellow mb-3">Connect your YouTube channel in Settings to enable uploads.</p>}
                    <input type="file" accept="video/*" data-testid="video-file-input" onChange={(e) => setFile(e.target.files[0])}
                      className="block w-full text-xs text-white/60 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-white/10 file:text-white file:text-xs file:cursor-pointer mb-3" />
                    <div className="mb-3">
                      <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 block font-mono">Schedule publish (optional · YouTube auto-publishes)</label>
                      <input type="datetime-local" data-testid="publish-at-input" value={publishAt} onChange={(e) => setPublishAt(e.target.value)}
                        className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-brand [color-scheme:dark]" />
                    </div>
                    <div className="flex gap-2">
                      <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} data-testid="privacy-select"
                        className="bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-brand">
                        <option value="private" className="bg-[#121212]">Private</option>
                        <option value="unlisted" className="bg-[#121212]">Unlisted</option>
                        <option value="public" className="bg-[#121212]">Public</option>
                      </select>
                      <button onClick={upload} disabled={uploading || !ytConnected} data-testid="upload-youtube-btn"
                        className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-40 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2">
                        <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload Video"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
